/**
 * Данные разделов «Ещё» и деталей (S-08 … S-40). Тот же клиент api(), что у витрин:
 * Bearer + initData, ошибки — нейтральным текстом с сервера.
 */
import type { ProfitInputs, ProfitResult, TaxInputs, TaxRegime, TaxBase } from '@wb/contracts/profit'
import { api, type ApiResult, type ActionItem } from './client.js'

export interface LibraryPrompt { agent: string; title: string; text: string; version: string }
export interface PromptLibrary { note: string; items: LibraryPrompt[] }
export const fetchPrompts = (): Promise<ApiResult<PromptLibrary>> => api<PromptLibrary>('/api/prompts')

export interface Blind {
  shop?: null
  reason?: string
  /** available: true — разрез по складам WB вернулся; false — по-прежнему скрыт; null — снимка ещё нет. */
  warehouses: { available: boolean | null; checked_display: string | null } | null
}
export const fetchBlind = (): Promise<ApiResult<Blind>> => api<Blind>('/api/blind')

/** Значение с готовой строкой; null — с причиной ([F-4.0.12]). value — только для геометрии. */
export interface Known { value: string | null; display: string | null; reason: string | null }
export type Tone = 'good' | 'attn' | 'risk' | 'fine' | 'neutral'
export interface Proposal { report_id: string; title: string; expected_effect: string | null; execution: string }

export interface Campaign {
  advert_id: number; name: string; status: { word: string; tone: Tone }; how_display: string; nm_ids: number[]
  spend: Known; daily_spend: Known; orders: Known; orders_rub: Known; drr: Known; cpo: Known
  views: Known; clicks: Known; ctr: Known; cpc: Known; cr: Known
  bid: Known; bid_min: Known; bid_recommended: Known; bid_recommended_reason: string | null
  budget: Known; lag: Known; insufficient: string | null
}
export interface Ads {
  shop?: null; reason?: string
  period_display: string; revenue_base_display: string
  totals: { spend: Known; orders: Known; revenue: Known; drr: Known; drr_delta: Known; prev_drr: Known; target_drr: Known; orders_share: Known }
  balance: { account: Known; netting: Known; bonus: Known; avg_daily_spend: Known; cover_days: Known }
  benchmarks: { drr_median: Known; cpo_median: Known }
  campaigns: Campaign[]
}
export const fetchAds = (): Promise<ApiResult<Ads>> => api<Ads>('/api/ads')

export interface CampaignFull {
  shop?: null; reason?: string
  period_display: string; campaign: Campaign
  products: Array<{ nm_id: number; title: string | null; stock: Known; days_of_stock: Known; minus_phrases: Known }>
  keywords: Array<{ phrase: string; nm_id: number; views: Known; clicks: Known; spend: Known; orders: Known; minus_candidate: boolean }>
  keywords_note: string
  proposals: Proposal[]
}
export const fetchCampaign = (id: string): Promise<ApiResult<CampaignFull>> => api<CampaignFull>(`/api/ads/${encodeURIComponent(id)}`)

export interface StockPlace {
  scope: 'wb' | 'seller'; name: string; size: string | null
  available: Known; in_way_to_client: Known; per_day: Known; days_of_stock: Known; recommended: Known
}
export interface StockItem {
  nm_id: number; title: string | null
  /** Артикул продавца и цвет: названия карточек WB повторяются, по ним товар не различить. */
  vendor_code: string | null; color: string | null
  availability: { word: string; tone: Tone } | null
  low_days: boolean; min_days: number | null; places: StockPlace[]
}
export interface Stock {
  shop?: null; reason?: string
  /** Окно, по которому посчитан темп продаж, в днях (просьба админа 27.10.2026). */
  window_days: number
  period_display: string; low_display: string | null; high_display: string | null
  summary: { low_pcs: number; overstock_pcs: number; items_pcs: number }
  items: StockItem[]
  overstock: Array<{ nm_id: number; title: string | null; available: Known; days_of_stock: Known; frozen: Known }>
  deadlines: { lead_time: Known; target_days: Known; safety: Known; next_slot: Known }
  warehouse_detail_available: boolean; supply_planning_available: boolean
  notes: string[]; proposals: Proposal[]
}
export const fetchStock = (days?: number): Promise<ApiResult<Stock>> =>
  api<Stock>(days === undefined ? '/api/stock' : `/api/stock?days=${encodeURIComponent(String(days))}`)

export interface FeedbackItem {
  id: string; kind: 'feedback' | 'question'; rating: number | null; preview: string | null
  product: string | null; nm_id: number | null; date_display: string
  draft_ready: boolean; answered: boolean; photos_pcs: number
}
export interface FeedbackList {
  shop?: null; reason?: string
  kind: 'feedback' | 'question'; state: 'waiting' | 'answered'
  counts: { feedback_waiting: number; question_waiting: number }
  items: FeedbackItem[]; truncation: string | null
}
export const fetchFeedback = (kind: string, state: string): Promise<ApiResult<FeedbackList>> =>
  api<FeedbackList>(`/api/feedback?kind=${encodeURIComponent(kind)}&state=${encodeURIComponent(state)}`)

export interface FeedbackFull {
  shop?: null; reason?: string
  kind: 'feedback' | 'question'; id: string; rating: number | null; date_display: string
  product: string | null; nm_id: number | null; article: string | null; variant: string | null
  text: Array<{ label: string | null; text: string }>; tags: string[]; photos_pcs: number; has_video: boolean; warned: boolean
  status: string; answer: string | null; answer_note: string | null
  draft: { text: string; chars: number; report_id: string; effect: string | null } | null
  limits: { min: number; max: number }; limits_display: string
}
export const fetchFeedbackItem = (kind: string, id: string): Promise<ApiResult<FeedbackFull>> =>
  api<FeedbackFull>(`/api/feedback/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`)

export interface ActionQueue {
  shop?: null; reason?: string
  state: string; pending_pcs: number; can_decide: boolean; items: ActionItem[]
}
export const fetchActionQueue = (state: string): Promise<ApiResult<ActionQueue>> =>
  api<ActionQueue>(`/api/actions?state=${encodeURIComponent(state)}`)

export interface ActionFull extends ActionItem {
  shop?: null; reason?: string
  stop_condition: string | null; undo: string | null; execution: string
  params: Array<{ label: string; display: string }>; answer_text: string | null; nm_ids: number[]
  can_decide: boolean; reject_reasons: string[]
  my_verdict?: Verdict
}
export const fetchAction = (id: string): Promise<ApiResult<ActionFull>> => api<ActionFull>(`/api/actions/${encodeURIComponent(id)}`)
export const decideAction = (id: string, decision: 'done' | 'rejected', note?: string): Promise<ApiResult<{ ok: true }>> =>
  api<{ ok: true }>(`/api/actions/${encodeURIComponent(id)}/decision`, { method: 'POST', body: JSON.stringify({ decision, note }) })

export interface SeriesPoint { key: string; label: string; value: string | null; display: string | null; partial: boolean }
export interface SkuCard {
  shop?: null; reason?: string
  card: { nm_id: number; title: string | null; vendor_code: string | null; brand: string | null; subject: string | null; photo: string | null; wb_url: string }
  money: null | { period_display: string; found: boolean; revenue: Known; payout: Known; sold: Known; returned: Known; buyout: Known; ads: Known; margin: Known; margin_pct: Known
    /** Калькулятор прибыли: null — не заполнен. */
    calc: null | { profit_per_unit: string; margin_pct: string | null; since: string; week_units: number; week_profit: string | null } }
  week: { period_display: string; orders: string | null; order_sum: string | null; opens: string | null; to_cart: string | null; cart_to_order: string | null
    points: SeriesPoint[]; summary: string; note: string | null }
  /** null у stock, campaigns и feedback — раздел человеку не открыт (права выдаёт владелец). */
  stock: { availability: { word: string; tone: Tone } | null
    places: Array<{ scope: string; name: string; size: string | null; available: Known; per_day: Known; days_of_stock: Known }> } | null
  campaigns: Campaign[] | null
  feedback: { rating: string | null; count: number; waiting: number; last: Array<{ id: string; rating: number | null; date_display: string; text: string | null }> } | null
  cost: null | { display: string | null; reason: string | null; valid_from: string | null }
  advice: Proposal[]
}
export const fetchSkuCard = (nm: string): Promise<ApiResult<SkuCard>> => api<SkuCard>(`/api/sku/${encodeURIComponent(nm)}`)

export interface BarRowData { key: string; label: string; sub: string | null; value: string | null; display: string | null; role: 'current' | 'previous'; tone: Tone | null }
export interface Metric {
  shop?: null; reason?: string
  code: string; title: string; period_display?: string; compare_display?: string; explain?: string
  series?: { period_display: string; points: SeriesPoint[]; summary: string; note: string | null }
  contributors?: { title: string; rule: string; rows: Array<{ nm_id: number; title: string; now: string | null; delta: string | null; tone: Tone }>; truncation: string | null }
  campaigns?: Array<{ advert_id: number; name: string; drr: Known; spend: Known }>
  bars?: BarRowData[]; bars_summary?: string
  drivers?: Array<{ name: string; contribution: Known; now: Known; prev: Known; tone: Tone }>; drivers_note?: string
}
export const fetchMetric = (code: string): Promise<ApiResult<Metric>> => api<Metric>(`/api/metric/${encodeURIComponent(code)}`)

export interface SearchResult {
  shop?: null; reason?: string; q: string
  groups: Array<{ kind: 'products' | 'campaigns' | 'feedback'; title: string; items: Array<{ id: string; title: string; sub: string }> }>
}
export const fetchSearch = (q: string): Promise<ApiResult<SearchResult>> => api<SearchResult>(`/api/search?q=${encodeURIComponent(q)}`)

// ── настройки владельца ──────────────────────────────────────────────────────
const put = <T>(path: string, body: unknown): Promise<ApiResult<T>> => api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const post = <T>(path: string, body: unknown): Promise<ApiResult<T>> => api<T>(path, { method: 'POST', body: JSON.stringify(body) })

export interface CostList {
  shop?: null; reason?: string
  coverage: { known: number; total: number; display: string; pct: number }
  items: Array<{ nm_id: number; title: string; vendor_code: string | null; unit: string | null; since: string | null
    /** Калькулятор прибыли: прибыль с одной проданной штуки и маржа; null — не заполнен. */
    profit: string | null; margin: string | null }>
  calc: { filled: number; total: number }
}
export const fetchCosts = (): Promise<ApiResult<CostList>> => api<CostList>('/api/costs')
export interface CostItem {
  shop?: null; reason?: string
  nm_id: number; title: string; vendor_code: string | null; today_display: string
  current: { cogs: string | null; inbound: string | null; packaging: string | null; since: string } | null
  history: Array<{ since: string; unit: string }>
  prev_nm: number | null; next_nm: number | null; position: string
}
export const fetchCost = (nm: string): Promise<ApiResult<CostItem>> => api<CostItem>(`/api/costs/${encodeURIComponent(nm)}`)
export const saveCost = (nm: number, v: { cogs: string; inbound: string; packaging: string }): Promise<ApiResult<{ ok: true; unit_display: string }>> =>
  put(`/api/costs/${nm}`, v)
export const importCosts = (csv: string): Promise<ApiResult<{ ok: true; updated: number; skipped: string[]; not_found: string[]; summary: string }>> =>
  post('/api/costs/import', { csv })

export interface FinanceSettings {
  shop?: null; reason?: string
  regimes: Record<string, string>; bases: Record<string, string>; vat_rates: number[]
  limits: Record<string, { min: number; max: number; rate: number; kind: 'income' | 'profit' }>
  tax: { regime: string; rate: string; base: string; vat: string }
  fixed: { monthly: string; allocation: string }
  replenishment: { lead_time: string; safety: string; target_drr: string }
  thresholds: { low: string; high: string; target: string; defaults: { low: number; high: number } }
}
export const fetchFinanceSettings = (): Promise<ApiResult<FinanceSettings>> => api<FinanceSettings>('/api/finance-settings')
export const saveFinanceSettings = (v: Pick<FinanceSettings, 'tax' | 'fixed' | 'replenishment'> & { thresholds: { low: string; high: string; target: string } }): Promise<ApiResult<{ ok: true }>> =>
  put('/api/finance-settings', v)

export interface NotifySettings {
  shop?: null; reason?: string
  tz: string; enabled: boolean; quiet_from: string; quiet_to: string; gets_reports: boolean
  reports: Array<{ agent: string; title: string; time: string }>
  /** Правила сигналов открытых человеку разделов; on — его личный переключатель. */
  signals: Array<{ code: string; name: string; about: string; when: string; section: string; on: boolean }>
  evening: { on: boolean; time: string }
  /** Последние сигналы этого человека и что с ними стало («пришло», «в вечерней сводке»…). */
  recent: Array<{ title: string; subject: string | null; at: string; status: string }>
}
export const fetchNotify = (): Promise<ApiResult<NotifySettings>> => api<NotifySettings>('/api/notify')
export const saveNotify = (v: { enabled: boolean; quiet_from: string; quiet_to: string }): Promise<ApiResult<{ ok: true }>> => put('/api/notify', v)
export const setSignalRule = (code: string, on: boolean): Promise<ApiResult<{ ok: true }>> => put('/api/notify/rule', { code, on })
export const saveEvening = (v: { on: boolean; time: string }): Promise<ApiResult<{ ok: true }>> => put('/api/notify/evening', v)

export interface TimeLog {
  shop?: null; reason?: string
  today_display: string; operations: Array<{ code: string; title: string; where: string }>; has_baseline: boolean
  history: Array<{ kind: string; date: string; total: string; ops: string }>
  saved: { display: string | null; reason: string | null }
}
export const fetchTime = (): Promise<ApiResult<TimeLog>> => api<TimeLog>('/api/time')
export const saveTime = (minutes: Record<string, string>): Promise<ApiResult<{ ok: true; kind: string; total_display: string }>> => post('/api/time', { minutes })

export interface KeyInfo {
  shop?: null; reason?: string
  token: null | {
    state: { word: string; tone: Tone }; kind: string; read_only: boolean; expires_display: string; connected_display: string
    expiring_note: string | null; categories: Array<{ code: string; title: string; on: boolean; required: boolean }>; missing: string[]
  }
  sync: Array<{ source: string; title: string; at: string | null; ok: boolean }>
  revoke_note: string
}
export const fetchKeyInfo = (): Promise<ApiResult<KeyInfo>> => api<KeyInfo>('/api/keys/info')
export const revokeKey = (confirm: string): Promise<ApiResult<{ ok: true; changed: boolean }>> => post('/api/keys/revoke', { confirm })

export interface OpsPanel {
  shop?: null; reason?: string
  sections: Array<{ code: string; title: string; ok: boolean; rows: Array<{ title: string; value: string; sub: string | null }> }>
}
export const fetchOpsPanel = (): Promise<ApiResult<OpsPanel>> => api<OpsPanel>('/api/ops/panel')

export interface Members {
  shop?: null; reason?: string
  store: { title: string; since: string; key: string }
  people: Array<{ tg: number; name: string; username: string | null; role: string; pending: boolean; primary: boolean; is_staff: boolean
    since: string; seen: string | null
    /** Права сотрудника — переключатели; null у владельцев и у ждущих подтверждения. */
    rights: Array<{ key: string; label: string; hint: string; on: boolean }> | null }>
  invites: Array<{ id: string; link: string | null; until: string }>
  privacy: string[]
}
export const fetchMembers = (): Promise<ApiResult<Members>> => api<Members>('/api/members')
export const createInviteLink = (): Promise<ApiResult<{ ok: true; link: string; note: string }>> => post('/api/members/invite', {})
export const revokeInviteLink = (id: string): Promise<ApiResult<{ ok: boolean }>> => post(`/api/members/invites/${encodeURIComponent(id)}/revoke`, {})
export const confirmMemberCode = (tg: number, code: string): Promise<ApiResult<{ ok: true; name: string }>> => post(`/api/members/${tg}/confirm`, { code })
export const revokeMemberAccess = (tg: number): Promise<ApiResult<{ ok: true }>> => post(`/api/members/${tg}/revoke`, {})
export const setMemberPerm = (tg: number, perm: string, on: boolean): Promise<ApiResult<{ ok: true }>> => post(`/api/members/${tg}/perms`, { perm, on })

// ── оценки «полезно / мимо» и пересчёт отчёта (решение 13.10.2026: в приложении, не кнопками бота) ──
export type Verdict = 'useful' | 'miss' | null
export const setReportVerdict = (id: string, verdict: Verdict): Promise<ApiResult<{ ok: true; verdict: Verdict }>> =>
  put(`/api/reports/${encodeURIComponent(id)}/feedback`, { verdict })
export const setActionVerdict = (id: string, verdict: Verdict): Promise<ApiResult<{ ok: true; verdict: Verdict }>> =>
  put(`/api/actions/${encodeURIComponent(id)}/feedback`, { verdict })

export interface AgentRunInfo { id: string; state: 'queued' | 'sent' | 'done' | 'failed'; report_id: string | null; at_display: string; message: string | null }
export interface AgentRun { last: AgentRunInfo | null; left: number }
export const fetchAgentRun = (agent: string): Promise<ApiResult<AgentRun>> => api<AgentRun>(`/api/agents/${encodeURIComponent(agent)}/run`)
export const startAgentRun = (agent: string): Promise<ApiResult<{ ok: true; last: AgentRunInfo }>> =>
  post(`/api/agents/${encodeURIComponent(agent)}/run`, {})

// ── «Спросить ИИ» (S-41…S-43) ────────────────────────────────────────────────
export interface ChatThreads {
  shop?: null; reason?: string
  left: number; limit: number; max_chars: number
  items: Array<{ id: string; title: string; at: string; questions: number }>
}
export interface ChatMessageView { id: string; role: 'user' | 'assistant'; content: string; tools: string[]; ungrounded: string[]; at: string; failed: boolean }
export interface ChatThread {
  shop?: null; reason?: string
  id: string; title: string; left: number; limit: number; messages: ChatMessageView[]
}
export const fetchChatThreads = (): Promise<ApiResult<ChatThreads>> => api<ChatThreads>('/api/chat/threads')
export const fetchChatThread = (id: string): Promise<ApiResult<ChatThread>> => api<ChatThread>(`/api/chat/threads/${encodeURIComponent(id)}`)
export const archiveChatThread = (id: string): Promise<ApiResult<{ ok: true }>> => post(`/api/chat/threads/${encodeURIComponent(id)}/archive`, {})

/** Таблица, прикреплённая к разговору: того, чего нет в API Wildberries (просьба админа 27.10.2026). */
export interface ChatFile {
  id: string; filename: string; format: string; sheet_name: string | null
  columns: string[]; rows_total: number; note: string | null
  size_display: string; at: string; skipped_note?: string
}
export interface ChatFiles { shop?: null; reason?: string; items: ChatFile[]; limit: number }
export const fetchChatFiles = (): Promise<ApiResult<ChatFiles>> => api<ChatFiles>('/api/chat/files')
export const uploadChatFile = (filename: string, content_base64: string, note?: string): Promise<ApiResult<{ ok: true; item: ChatFile }>> =>
  post('/api/chat/files', { filename, content_base64, ...(note === undefined ? {} : { note }) })
export const deleteChatFile = (id: string): Promise<ApiResult<{ ok: true }>> =>
  api<{ ok: true }>(`/api/chat/files/${encodeURIComponent(id)}`, { method: 'DELETE' })

// ── Калькулятор прибыли (S-27): по каждой модели, как в кабинете WB ─────────
export interface CalcData {
  shop?: null; reason?: string
  nm_id: number; title: string; vendor_code: string | null; photo: string | null; can_edit: boolean; today: string
  regimes: Record<TaxRegime, { title: string; rate: number; min: number; max: number; kind: 'income' | 'profit'; min_tax_pct: number }>
  bases: Record<TaxBase, string>; vat_rates: number[]
  prefill: { inputs: ProfitInputs; source: Partial<Record<keyof ProfitInputs, string>>
    facts: { net_sold: number; sold: number; returned: number; penalties_per_unit: number | null; buyout_pct: number | null; window: string } }
  saved: null | { valid_from: string; since: string; is_first: boolean; state: CalcVersion['state']; inputs: ProfitInputs; tax: TaxInputs; result: ProfitResult }
  tax: TaxInputs; tax_is_set: boolean; tax_warning: string | null
  week: null | { period: string; net_units: number; profit_rub: number }
  /** Все расчёты по датам начала действия, новые сверху. */
  versions: CalcVersion[]
  prev_nm: number | null; next_nm: number | null; position: string
}
export interface CalcVersion { valid_from: string; since: string; profit_rub: number; margin_pct: number | null; cogs_rub: number; state: 'past' | 'current' | 'future' }
export const fetchCalc = (nm: string, at: string | null = null): Promise<ApiResult<CalcData>> =>
  api<CalcData>(`/api/calc/${encodeURIComponent(nm)}${at === null ? '' : `?at=${encodeURIComponent(at)}`}`)
export const saveCalc = (nm: number, v: { inputs: ProfitInputs; tax: TaxInputs; valid_from: string | null }): Promise<ApiResult<{ ok: true; result: ProfitResult; valid_from: string; since: string }>> =>
  put(`/api/calc/${nm}`, v)
export const deleteCalc = (nm: number, from: string): Promise<ApiResult<{ ok: true }>> =>
  api(`/api/calc/${nm}/${encodeURIComponent(from)}`, { method: 'DELETE' })

/**
 * S-44 «Прибыль за период» — фактическая (админ 23.10.2026): себестоимость по партиям и прочие расходы владельца —
 * постоянные, цена, комиссия, логистика, реклама и НДС — факт каждого дня по отчётам WB.
 */
export interface ProfitSlice {
  shop?: null; reason?: string
  from: string; to: string; today: string; period_display: string
  presets: Array<{ key: string; label: string; from: string; to: string }>
  data_to: string | null; fresh_to: string | null; data_note: string | null
  /**
   * profit_rub — прибыль моделей (без штрафов и удержаний без товара); result_rub — с ними; prelim_rub — из profit_rub
   * за дни по оперативной статистике (предварительно); unknown_* — проданное без себестоимости.
   */
  total: { net_units: number; revenue: number; to_transfer: number; wb: number; cogs: number; ads: number; vat: number; other: number
    profit_rub: number; shop_net: number; penalty_rub: number; result_rub: number; prelim_rub: number; unknown_units: number; unknown_models: number }
  group: 'day' | 'week'
  rows: Array<{ key: string; label: string; net_units: number; revenue: number; profit_rub: number; missing_units: number; prelim: boolean }>
  products: Array<{ nm_id: number; title: string; vendor_code: string | null; net_units: number; revenue: number; to_transfer: number; wb: number
    cogs: number; ads: number; vat: number; other: number; profit_rub: number | null; avg_price: number | null; per_unit: number | null
    margin_pct: number | null; plan_per_unit: number | null; prelim: boolean; batches: Array<{ since: string; units: number; unit_cost: number }> }>
  missing: Array<{ nm_id: number; title: string; net_units: number }>
}
export const fetchProfit = (from: string | null, to: string | null): Promise<ApiResult<ProfitSlice>> =>
  api<ProfitSlice>('/api/profit' + (from !== null && to !== null ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : ''))

// ── S-45 «Архив месяцев»: выгрузка закрывшегося месяца в Excel (заказчик 02.11.2026) ──────────
export interface ArchiveItem {
  month: string
  built_at: string
  /** false — на момент сборки отчёты WB пришли не за все дни месяца. */
  complete: boolean
  rows_total: number
  size_bytes: number
  sha256: string
  in_cloud: boolean
  totals: Record<string, number | null>
}
export interface ArchivesSlice {
  shop_id: string | null
  reason?: string
  items: ArchiveItem[]
  /** Закончившиеся месяцы, по которым файла ещё нет. */
  can_build: string[]
}
export const fetchArchives = (): Promise<ApiResult<ArchivesSlice>> => api<ArchivesSlice>('/api/archives')
export const buildArchive = (month: string): Promise<ApiResult<{ ok: true; item: ArchiveItem }>> =>
  api<{ ok: true; item: ArchiveItem }>(`/api/archives/${month}/build`, { method: 'POST' })
export const sendArchive = (month: string): Promise<ApiResult<{ ok: true; sent: boolean }>> =>
  api<{ ok: true; sent: boolean }>(`/api/archives/${month}/send`, { method: 'POST' })
